<template>
  <InputWrapper
    v-model="model"
    className="input-row--text"
    :name="name"
    :placeholder="placeholder || (mask ?? undefined)"
    :error="error"
    :label="label"
    :disabled="disabled ?? false"
    :helperText="helperText"
    :required="required"
    :characterLimit="characterLimit"
    :prefixText="prefixText"
    :suffixText="suffixText"
    :showRequired="showRequired"
    v-bind="$attrs"
  >
    <template #icon>
      <slot name="icon"></slot>
    </template>

    <template #input>
      <input
        :id="`${name}`"
        :name="`${name}`"
        ref="input-text"
        :type="inputType"
        v-model="model"
        v-maska="mask ?? undefined"
        :placeholder="placeholder ?? mask ?? undefined"
        class="form-input form-input--text"
        :disabled="disabled ?? undefined"
        @blur="onBlur"
        @keyup.enter="onEnter"
        @keypress="onKeypress"
      />

      <Button
        v-if="!disabled && model?.length"
        @click="clearField"
        severity="tertiary"
        alt="Clear field"
      >
        <template #iconLeft>
          <IconClose />
        </template>
      </Button>

      <Button
        v-if="passwordMask"
        @click="toggleMask"
        severity="tertiary"
        alt="Toggle mask"
      >
        <template #iconLeft>
          <IconEyeVisible v-if="isMasked" />
          <IconEyeHidden v-else />
        </template>
      </Button>
    </template>

    <template #iconRight>
      <slot name="iconRight"></slot>
    </template>
  </InputWrapper>
</template>

<script setup lang="ts">
import { vMaska } from 'maska/vue';
import { computed, nextTick, ref, useTemplateRef } from 'vue';

import InputWrapper from './InputWrapper.vue';
import Button from '../global/Button.vue';

import IconEyeVisible from '@/icons/fields/IconEyeVisible.svg?component';
import IconEyeHidden from '@/icons/fields/IconEyeHidden.svg?component';
import IconClose from '@/icons/toast/IconClose.svg?component';

const model = defineModel<string>();
const emit = defineEmits(['onEnter', 'onBlur', 'onKeypress']);

const inputText = useTemplateRef('input-text');
const isMasked = ref(true);

const props = withDefaults(
  defineProps<{
    /**
     *  A label is required for text fields and succinctly describes the field’s purpose. If another text  element is acting as the label, like a section title, then the label can be visually hidden.
     */
    label?: string;

    /**
     *  Helper text is placed below the field and informs the user of any  requirements or disclaimers. Helper text can be shown persistently or on focus (active state). Helper text will be replaced with an error message if an error occurs.
     */
    helperText?: string;

    /**
     *  Text for the the error if one exists. This overwrites Helper Text
     */
    error?: string;

    /**
     * Static text or symbols can be prepended to the input. The prefix clarifies the expected input and removes the need for users to manually enter them. Useful for values like currency.
     */
    prefixText?: string;

    /**
     * Static text or symbols can be appended to the input. The suffix clarifies the expected input and removes the need for users to manually  enter them. These are useful for entering values like dimensions and  weight or currencies that use trailing symbols.
     */
    suffixText?: string;

    /**
     * Whether the field is editable or read-only
     */
    disabled?: boolean | null;

    /**
     *  An asterisk will display next to the label to indicate a field is required. The word “optional” in parentheses will appear next to the field label if the field isn’t required. If most fields are required, only indicate the optional fields. If most fields are optional, only indicate the required fields.
     */
    required?: boolean;

    /**
     *  You can hide the * or (optional) tags for one-off inputs like search fields.
     */

    showRequired?: boolean;

    /**
     *  Placeholder text is optional and remains visible until one or more characters are entered. Because the placeholder text disappears, putting instructions or requirements as placeholder text is not accessible. Helper text is the preferred  method to convey this information.
     */
    placeholder?: string;

    /**
     * A character counter will appear when there is a limit on the number of characters. The counter updates in real-time as the characters are added or removed.
     */
    characterLimit?: number;

    /**
     * Populates both the name and id (also ties the label to the input). Required for accessibility.
     */
    name?: string;

    /**
     * Formatting for things like phone number or credit card styles
     */
    mask?: string;

    /**
     * Whether this is a Password field with a toggleable Mask.
     */
    passwordMask?: boolean;

  }>(),
  {
    label: '',
    required: false,
    disabled: null,
    showRequired: false,
    name: '',
    error: '',
    placeholder: '',
    passwordMask: false,
  },
);

const onBlur = (e: FocusEvent) => {
  emit('onBlur', e);
};

const onKeypress = (e: KeyboardEvent) => {
  emit('onKeypress', e);
};

const onEnter = (e: KeyboardEvent) => {
  emit('onEnter', e);
};

const inputType = computed(() => {
  return isMasked.value && props.passwordMask ? 'password' : 'text';
});

const toggleMask = (e: Event) => {
  e.preventDefault();
  isMasked.value = !isMasked.value;
};

const clearField = () => {
  model.value = '';

  nextTick(() => {
    emit('onBlur');
  });
};

defineExpose({
  inputText,
});
</script>

<style lang="scss">
.input-row--text {
  input {
    appearance: none;
    border: 0;
    outline: 0;
    padding: 0;
  }
}
</style>